import { isDefined } from "@clipboard-health/util-ts";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonHeader,
  IonLabel,
  IonRow,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import { get } from "@src/appV2/api";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { identifyUser } from "@src/appV2/FeatureFlags/identifyUser";
import { useToast } from "@src/appV2/lib";
import {
  getHasOpenPlacementsNearbyApiUrl,
  hasOpenPlacementsNearbyResponseSchema,
} from "@src/appV2/redesign/PlacementCandidate/api/useGetHasOpenPlacementsNearby";
import { useIsSurfaceShiftsEnabled } from "@src/appV2/redesign/ShiftDiscovery/useIsSurfaceShiftsEnabled";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useWorkerDocumentsByStatus } from "@src/appV2/Worker/useWorkerDocumentsByStatus";
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from "launchdarkly-react-client-sdk";
import { isEmpty } from "lodash";
import { FC, FormEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import timezoneLookup from "tz-lookup";

import Alert from "./Alert";
import { showSSNPage, updateAgentData } from "./api";
import { AddressPageData, OnBoardingComponentProps } from "./model";
import { StyledIonPage } from "./style";
import { ErrorAlert } from "./types";
import { LocationEdit } from "../../address/locationEdit";
import { Place } from "../../address/types";
import { OnboardingRouterPath } from "../../routing/constant/onboardingRoute";
import { ONBOARDING_SEGMENT_EVENT_NAMES } from "../constants/ONBOARDING_SEGMENT_EVENT_NAMES";
import { ONBOARDING_STAGES } from "../constants/ONBOARDING_STAGES";
import { logOnboardingError } from "../util/logging";
import { getOigState } from "../util/oig";
import { fireOnboardingSegmentEvent } from "../util/segment";
// eslint-disable-next-line no-restricted-imports

async function getNextStagePath(): Promise<string | undefined> {
  const oig = getOigState();
  if (oig?.dobRequired) {
    return OnboardingRouterPath.ONBOARDING_DOB;
  }

  const { showSSNFlag } = await showSSNPage();
  if (showSSNFlag) {
    return OnboardingRouterPath.ONBOARDING_SSN;
  }
}

const AgentAddress: FC<
  Omit<OnBoardingComponentProps, "nextStagePath"> & {
    nextStagePath: (isSurfaceShiftsEnabled: boolean, isSurfaceJobsFlowValid: boolean) => string;
  }
> = ({ nextStagePath }) => {
  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();
  const { showErrorToast } = useToast();
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [updatingData, setUpdatingData] = useState<boolean>(false);
  const [errorAlert, setErrorAlert] = useState<ErrorAlert>({ hasError: false });
  const ldClient = useLDClient();

  // This is required to identify the user in LD
  const documentsByStatus = useWorkerDocumentsByStatus();

  const [addressData, setAddressData] = useState<AddressPageData | Partial<AddressPageData>>({});

  const {
    data: worker,
    isLoading: isLoadingWorker,
    refetch: refetchWorker,
  } = useGetWorker({ enabled: false });

  const history = useHistory();

  const onNext = async (event?: FormEvent | MouseEvent): Promise<void> => {
    if (!isDefined(worker)) {
      return;
    }

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    event && event.preventDefault();
    setErrorAlert({ hasError: false });
    try {
      setUpdatingData(true);

      let tmz: string | undefined;

      if (addressData?.location?.lat && addressData?.location?.lng) {
        tmz = timezoneLookup(addressData.location.lat, addressData.location.lng);
      }

      await updateAgentData({
        stage: ONBOARDING_STAGES.ADDRESS,
        address: addressData.address,
        location: addressData.location,
        tmz,
      });

      fireOnboardingSegmentEvent(ONBOARDING_SEGMENT_EVENT_NAMES.ENTERED_ADDRESS, {
        hcpId: worker.userId,
        hcpEventValue: addressData?.address?.formatted,
        email: worker.email,
      });

      // Refetch the worker so we can get the latest worker information (specifically the MSA) as it is not returned by the `updateAgent` onboarding API
      const lastestWorkerResponse = await refetchWorker();
      const updatedWorker = lastestWorkerResponse.data;

      // Re-identify the user so that the worker's MSA is updated in LD
      if (ldClient) {
        await identifyUser({
          inputForContext: {
            worker: updatedWorker,
            documentsByStatus,
          },
          client: ldClient,
        });
      }

      // Evaluate the surface jobs flag again with the updated MSA by directly calling the launch darkly client
      // This is necessary because at this point the value returned by the useCbhFlag will be outdated as we have just updated the LD user's MSA
      // Note: after this point we can call `useCbhFlag` to get the value of the feature flag, because the user's context will be correct when the components are mounted
      const isSurfaceJobsEnabled = ldClient?.variation(CbhFeatureFlag.SURFACE_JOBS, false);
      let isSurfaceJobsFlowValid = false;

      // Check if there are any placements nearby
      if (isSurfaceJobsEnabled) {
        const hasOpenPlacementsNearbyResponse = await get({
          url: getHasOpenPlacementsNearbyApiUrl(worker.userId),
          responseSchema: hasOpenPlacementsNearbyResponseSchema,
        });

        if (hasOpenPlacementsNearbyResponse.data.data.attributes.hasOpenPlacementsNearby) {
          isSurfaceJobsFlowValid = true;
        }
      }

      const next =
        (await getNextStagePath()) ?? nextStagePath(isSurfaceShiftsEnabled, isSurfaceJobsFlowValid);
      history.push(String(next));
    } catch (error) {
      if (!error.response || !error.response.text || error.response.status >= 500) {
        logOnboardingError(ONBOARDING_STAGES.ADDRESS, (error as Error).message, worker.userId);
        setErrorAlert({ hasError: true, reason: error?.message });
        return;
      }
      showErrorToast("Unexpected error occurred while saving address");
    } finally {
      setUpdatingData(false);
    }
  };

  useEffect(() => {
    if (!isDefined(worker)) {
      return;
    }
    // important. we need to setLoadingData true here
    // in case the user navigates back and forth through the screens

    // loadingData forces a re-render of the LoadingEdit component
    // when our state in this component changes
    setLoadingData(true);
    const { geoLocation, address } = worker;
    const lat = geoLocation?.coordinates?.[1];
    const lng = geoLocation?.coordinates?.[0];
    if (!isEmpty(address)) {
      setAddressData({
        address,
        location: lat && lng ? { lat, lng } : undefined,
        searchLocation: address?.formatted ?? "",
      });
    }
    setLoadingData(false);
  }, [worker]);

  const onPlaceChange = (place: Place) => {
    if (isEmpty(place.location) || isEmpty(place.address)) {
      return;
    }

    setAddressData({
      address: place.address,
      location: place.location,
      searchLocation: place.address.formatted ?? "",
    });
  };

  const closeErrorModal = () => {
    setErrorAlert({ hasError: false });
  };

  return (
    <StyledIonPage className="onboarding-page">
      <IonHeader no-border className="onboarding-header">
        <IonToolbar className="onboarding-toolbar">
          <IonButtons slot="start">
            <IonBackButton text="" defaultHref="/home/agentEmail" mode="ios" color="dark" />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <Alert
          isOpen={errorAlert.hasError}
          reason={errorAlert.reason}
          closeAlert={closeErrorModal}
        />

        <div className="signup-content content-layout">
          <div className="form-container">
            <form onSubmit={onNext}>
              <IonRow>
                <IonCol>
                  <div className="form-heading">
                    <h4>Where do you live?</h4>
                  </div>

                  <div>
                    <IonLabel className="form-label">Home Address</IonLabel>
                    {!loadingData && (
                      <LocationEdit
                        searchLocation={addressData.searchLocation ?? ""}
                        onPlaceChange={onPlaceChange}
                        includeLine1Input
                        initAddress={addressData}
                      />
                    )}
                  </div>
                </IonCol>
              </IonRow>
            </form>
          </div>

          <div className="signupform-footer footer-container">
            <IonButton
              expand="block"
              size="large"
              class="ion-margin-top ion-margin-bottom continue-button"
              disabled={
                updatingData ||
                !addressData.address ||
                !addressData.address.formatted ||
                isLoadingWorker
              }
              onClick={onNext}
            >
              Continue
              {updatingData && <IonSpinner slot="end" class="ion-margin-start" name="lines" />}
            </IonButton>
          </div>
        </div>
      </IonContent>
    </StyledIonPage>
  );
};

export { AgentAddress };
